import type { RecordedAgenticChatSession } from 'features/embeddedSidebar/hooks/useAgenticChatSessionHistory'
import { isClientDocChatTurnId } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatConversation'

import {
  completedSuccessfulTurns,
  getLastCompletedTurnId,
  shouldUnarchiveOnFollowUp,
} from './documentChatConversationPersistence'

/** Recency is paged; pinned chats older than that window still need to appear. */
export function mergePinnedSessionsIntoRecency(
  recencySessions: RecordedAgenticChatSession[],
  pinnedSessions: RecordedAgenticChatSession[]
): RecordedAgenticChatSession[] {
  const sessionsByKey = new Map(
    recencySessions.map((session) => [session.key, session])
  )
  pinnedSessions.forEach((session) => {
    const existing = sessionsByKey.get(session.key)
    if (!existing) {
      sessionsByKey.set(session.key, session)
      return
    }
    sessionsByKey.set(session.key, {
      ...existing,
      pinned: existing.pinned ?? session.pinned,
      pinnedAt: existing.pinnedAt ?? session.pinnedAt,
    })
  })
  return [...sessionsByKey.values()]
}

function liveSessionCoversInProgress(
  liveSessions: RecordedAgenticChatSession[],
  persisted: RecordedAgenticChatSession
): boolean {
  if (liveSessions.some((live) => live.key === persisted.key)) return false
  if (persisted.lastCompletedTurnId !== undefined) return false
  const query = persisted.pendingUserQuery
  if (!query) return false
  return liveSessions.some((live) => {
    if (live.conversation.id) return false
    return live.conversation.turns[0]?.query === query
  })
}

export function mergeLiveAndPersistedSessions({
  liveSessions,
  persistedSessions,
}: {
  liveSessions: RecordedAgenticChatSession[]
  persistedSessions: RecordedAgenticChatSession[]
}): RecordedAgenticChatSession[] {
  const sessionsByKey = new Map<string, RecordedAgenticChatSession>()
  persistedSessions.forEach((session) => {
    sessionsByKey.set(session.key, session)
  })
  liveSessions.forEach((liveSession) => {
    const persisted = sessionsByKey.get(liveSession.key)
    if (!persisted) {
      sessionsByKey.set(liveSession.key, {
        ...liveSession,
        isHydrated: true,
      })
      return
    }
    sessionsByKey.set(liveSession.key, {
      ...persisted,
      ...liveSession,
      archived:
        persisted.archivedOverride ??
        (persisted.syncStatus === 'archiving' ||
        persisted.syncStatus === 'unarchiving'
          ? persisted.archived
          : (liveSession.archived ?? persisted.archived)),
      conversation: liveSession.conversation,
      isHydrated: true,
      lastCompletedTurnId:
        getLastCompletedTurnId(liveSession.conversation.turns) ??
        persisted.lastCompletedTurnId,
      persistError: liveSession.persistError ?? persisted.persistError,
      pinned: liveSession.pinned ?? persisted.pinned,
      pinnedAt: liveSession.pinnedAt ?? persisted.pinnedAt,
      syncStatus:
        persisted.syncStatus === 'archiving' ||
        persisted.syncStatus === 'unarchiving' ||
        persisted.syncStatus === 'deleting'
          ? persisted.syncStatus
          : (liveSession.syncStatus ?? persisted.syncStatus),
      title: liveSession.customTitle ?? persisted.title ?? liveSession.title,
      updatedAt: Math.max(liveSession.updatedAt, persisted.updatedAt),
      visibleTurnCount:
        completedSuccessfulTurns(liveSession.conversation.turns).length ||
        persisted.visibleTurnCount,
    })
  })
  return [...sessionsByKey.values()]
    .filter((session) => !liveSessionCoversInProgress(liveSessions, session))
    .sort(compareAgenticChatHistorySessions)
}

/** Pinned chats stay above unpinned; among pinned, newest pin first, then recency. */
export function compareAgenticChatHistorySessions(
  left: RecordedAgenticChatSession,
  right: RecordedAgenticChatSession
): number {
  const leftPinned = left.pinned === true
  const rightPinned = right.pinned === true
  if (leftPinned !== rightPinned) return leftPinned ? -1 : 1
  if (leftPinned) {
    const pinDelta = (right.pinnedAt ?? 0) - (left.pinnedAt ?? 0)
    if (pinDelta !== 0) return pinDelta
  }
  return right.updatedAt - left.updatedAt
}

/**
 * Live turns the backend has already written (unarchive on claim, ui_history on
 * completion) that the loaded history rows do not show yet. One key per turn.
 */
export function liveTurnsAwaitingHistory({
  liveSessions,
  persistedSessions,
}: {
  liveSessions: RecordedAgenticChatSession[]
  persistedSessions: RecordedAgenticChatSession[]
}): string[] {
  return liveSessions.flatMap((live) => {
    if (!live.conversation.id) return []
    const persisted = persistedSessions.find(
      (session) => session.key === live.key
    )
    if (
      persisted?.archived === true &&
      shouldUnarchiveOnFollowUp({
        archived: true,
        turns: live.conversation.turns,
      })
    ) {
      return [`${live.key}:unarchive:${live.conversation.activeTurnId ?? ''}`]
    }
    const turnId = getLastCompletedTurnId(live.conversation.turns)
    if (!turnId || !isClientDocChatTurnId(turnId)) return []
    if (persisted?.lastCompletedTurnId === turnId) return []
    return [`${live.key}:${turnId}`]
  })
}

export function excludeDeletedHistorySessions(
  sessions: RecordedAgenticChatSession[],
  deletedSessionKeys: ReadonlySet<string>
): RecordedAgenticChatSession[] {
  if (deletedSessionKeys.size === 0) return sessions
  return sessions.filter((session) => !deletedSessionKeys.has(session.key))
}

export function splitActiveAndArchivedSessions(
  sessions: RecordedAgenticChatSession[]
): {
  activeSessions: RecordedAgenticChatSession[]
  archivedSessions: RecordedAgenticChatSession[]
} {
  const activeSessions: RecordedAgenticChatSession[] = []
  const archivedSessions: RecordedAgenticChatSession[] = []
  sessions.forEach((session) => {
    if (
      session.syncStatus === 'archiving' ||
      session.syncStatus === 'unarchiving' ||
      session.syncStatus === 'deleting'
    ) {
      if (session.archived) archivedSessions.push(session)
      else activeSessions.push(session)
      return
    }
    const unarchiving = shouldUnarchiveOnFollowUp({
      archived: session.archived === true,
      turns: session.conversation.turns,
    })
    if (unarchiving || !session.archived) {
      activeSessions.push(
        unarchiving ? { ...session, archived: false } : session
      )
      return
    }
    archivedSessions.push(session)
  })
  return { activeSessions, archivedSessions }
}
